/**
 * Frontier Index — directory of every catalogue + count + jump-link.
 *
 * The intelligence-surface map: at a glance, how many entries we track
 * across each dimension, with deep-links into the relevant page/tab.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Compass, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Summary {
  summary: Record<string, number>
  totals: { static_entries: number; live_sources: string[] }
  generated_at: string
}

type Group = {
  title: string
  page: string  // /app/...
  tone: string
  items: { key: string; label: string; tab?: string }[]
}

const GROUPS: Group[] = [
  {
    title: 'Frontier orgs + models',
    page: '/app/frontier',
    tone: 'border-purple/40',
    items: [
      { key: 'releases',     label: 'Model releases',          tab: undefined },
      { key: 'open_models',  label: 'Open-weight models',      tab: 'open_models' },
      { key: 'national_strategies', label: 'National strategies' },
      { key: 'benchmarks',   label: 'Capability benchmarks' },
    ],
  },
  {
    title: 'People + bodies',
    page: '/app/knowledge-base',
    tone: 'border-blue/40',
    items: [
      { key: 'profiles',         label: 'People profiles' },
      { key: 'coalitions',       label: 'Coalitions / consortia' },
      { key: 'open_letters',     label: 'Open letters / declarations' },
      { key: 'departures',       label: 'Departures / exits' },
      { key: 'policy_testimonies', label: 'Policy testimonies' },
    ],
  },
  {
    title: 'Industry events',
    page: '/app/industry-watch',
    tone: 'border-amber/40',
    items: [
      { key: 'lawsuits',        label: 'Lawsuits' },
      { key: 'acquisitions',    label: 'Acquisitions / M&A' },
      { key: 'funding_rounds',  label: 'Funding rounds' },
      { key: 'conferences',     label: 'Conferences / summits' },
      { key: 'startup_graveyard', label: 'Startup graveyard' },
      { key: 'data_deals',      label: 'Data licensing deals' },
      { key: 'hypergrowth_apps', label: 'Hypergrowth apps' },
      { key: 'agents_launches', label: 'Agent launches' },
    ],
  },
  {
    title: 'Compute + infra',
    page: '/app/industry-watch',
    tone: 'border-emerald/40',
    items: [
      { key: 'compute_buildouts', label: 'Datacenter buildouts' },
      { key: 'compute_pipeline',  label: 'Chip pipeline' },
      { key: 'energy_deals',      label: 'Energy / nuclear deals' },
      { key: 'named_clusters',    label: 'Named clusters' },
    ],
  },
  {
    title: 'Safety / governance',
    page: '/app/knowledge-base',
    tone: 'border-rose/40',
    items: [
      { key: 'incidents',     label: 'AI Safety Incidents' },
      { key: 'aisi_evals',    label: 'AISI / METR / Apollo evals' },
      { key: 'threat_models', label: 'Threat models' },
      { key: 'jailbreaks',    label: 'Jailbreak techniques' },
      { key: 'cyber_ai',      label: 'Cyber AI' },
      { key: 'bans',          label: 'Bans / restrictions' },
      { key: 'regulations',   label: 'Regulation tracker' },
    ],
  },
  {
    title: 'Knowledge canon',
    page: '/app/knowledge-base',
    tone: 'border-cyan/40',
    items: [
      { key: 'landmark_papers', label: 'Landmark papers' },
      { key: 'datasets',        label: 'Training datasets' },
      { key: 'evals_tooling',   label: 'Evals tooling' },
      { key: 'books',           label: 'Books / canon' },
      { key: 'projections',     label: 'Projections / forecasts' },
      { key: 'capability_moments', label: 'Capability moments' },
      { key: 'robotics',        label: 'Robotics platforms' },
      { key: 'scientific_ai',   label: 'Scientific AI' },
      { key: 'defence_ai',      label: 'Defence AI' },
    ],
  },
]

const PAGE_LINKS: { label: string; href: string; tone: string }[] = [
  { label: 'Frontier Models',    href: '/app/frontier',           tone: 'text-purple-light' },
  { label: 'Frontier Globe',     href: '/app/frontier-globe',     tone: 'text-purple-light' },
  { label: 'Notable Models (Epoch · live)', href: '/app/notable-models', tone: 'text-emerald' },
  { label: 'Trending Papers (HF · live)',   href: '/app/trending-papers', tone: 'text-blue-light' },
  { label: 'arXiv Firehose (live)', href: '/app/arxiv',           tone: 'text-blue-light' },
  { label: 'Capability Benchmarks', href: '/app/benchmarks',      tone: 'text-amber-light' },
  { label: 'National Strategies', href: '/app/national-strategies', tone: 'text-blue-light' },
  { label: 'Industry Watch (19 tabs)', href: '/app/industry-watch', tone: 'text-amber-light' },
  { label: 'Knowledge Base (14 tabs)', href: '/app/knowledge-base', tone: 'text-purple-light' },
  { label: 'AI Safety Incidents', href: '/app/ai-incidents',      tone: 'text-rose-light' },
  { label: 'Regulation Tracker', href: '/app/frontier-regulation', tone: 'text-blue-light' },
  { label: 'Frontier Defence',   href: '/app/frontier-defence',   tone: 'text-rose-light' },
]

export default function FrontierIndexPage() {
  const [data, setData] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    api.get('/api/frontier/index-summary')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const summary = data?.summary || {}

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Compass size={18} className="text-purple-light" /> FRONTIER INDEX
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          directory · the entire intelligence surface
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Static entries tracked" value={data?.totals.static_entries ?? 0} tone="purple" />
        <Tile label="Catalogues" value={Object.keys(summary).length} tone="blue" />
        <Tile label="Live sources" value={data?.totals.live_sources?.length ?? 0} tone="emerald" />
        <Tile label="Page surfaces" value={PAGE_LINKS.length} tone="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {GROUPS.map(g => (
          <section key={g.title} className={clsx('card p-3 border', g.tone)}>
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-sm font-semibold text-text-primary">{g.title}</h2>
              <Link to={g.page} className="text-[10px] text-blue-light hover:underline">open page →</Link>
            </div>
            <ul className="space-y-1">
              {g.items.map(item => (
                <li key={item.key} className="flex items-center justify-between text-xs">
                  <Link to={g.page} className="text-text-secondary hover:text-text-primary">
                    {item.label}
                  </Link>
                  <span className="font-mono text-amber-light">{summary[item.key] ?? '—'}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <section className="card p-3">
        <h2 className="text-sm font-semibold text-text-primary mb-2">Direct links</h2>
        <div className="flex flex-wrap gap-2">
          {PAGE_LINKS.map(l => (
            <Link key={l.href} to={l.href}
              className={clsx('px-2 py-1 rounded bg-surface-raised text-xs hover:bg-surface-raised/60', l.tone)}>
              {l.label}
            </Link>
          ))}
        </div>
      </section>

      {data && (
        <p className="text-[10px] text-text-muted text-center">
          Live feeds: {data.totals.live_sources.join(' · ')} · summary generated {data.generated_at?.slice(0, 16).replace('T', ' ')} UTC
        </p>
      )}
    </div>
  )
}

function Tile({ label, value, tone }: { label: string; value: number; tone: 'purple' | 'blue' | 'emerald' | 'amber' }) {
  const c = tone === 'purple' ? 'text-purple-light'
          : tone === 'blue' ? 'text-blue-light'
          : tone === 'emerald' ? 'text-emerald'
          : 'text-amber-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>{value.toLocaleString()}</div>
    </div>
  )
}
